/**
 * Parsers for various social media export formats.
 * Each parser takes a raw string (file content) and returns an array of ImportedPost.
 */

export interface ImportedPost {
  id: string;
  content: string;
  createdAt: number; // unix timestamp
  source: string;
  url?: string;
  selected: boolean;
}

// ── Twitter / X ────────────────────────────────────────────────────────────────

/**
 * Parse the tweets.js file from a Twitter/X data archive.
 * Twitter wraps the JSON in:  window.YTD.tweets.part0 = [...]
 */
export function parseTwitterArchive(raw: string): ImportedPost[] {
  try {
    // Strip the JS variable assignment wrapper if present
    const jsonStr = raw
      .replace(/^window\.YTD\.\w+\.\w+\s*=\s*/, '')
      .trim()
      .replace(/;$/, '');

    const data: unknown = JSON.parse(jsonStr);
    if (!Array.isArray(data)) return [];

    return data
      .map((item: unknown, idx: number): ImportedPost | null => {
        const tweet = (item as Record<string, unknown>).tweet ?? item;
        if (!tweet || typeof tweet !== 'object') return null;

        const t = tweet as Record<string, unknown>;
        const content = (t.full_text ?? t.text ?? '') as string;

        // Skip retweets
        if (content.startsWith('RT @')) return null;

        // Strip t.co URLs that Twitter appends to the end
        const cleaned = content.replace(/\s*https:\/\/t\.co\/\S+/g, '').trim();
        if (!cleaned) return null;

        const createdAt = t.created_at
          ? Math.floor(new Date(t.created_at as string).getTime() / 1000)
          : Math.floor(Date.now() / 1000);

        const tweetId = (t.id_str ?? t.id ?? String(idx)) as string;

        return {
          id: `twitter-${tweetId}`,
          content: cleaned,
          createdAt,
          source: 'Twitter/X',
          selected: true,
        };
      })
      .filter((p): p is ImportedPost => p !== null);
  } catch {
    return [];
  }
}

// ── Mastodon ───────────────────────────────────────────────────────────────────

/**
 * Parse the outbox.json file from a Mastodon account export.
 */
export function parseMastodonExport(raw: string): ImportedPost[] {
  try {
    const data: unknown = JSON.parse(raw);
    const obj = data as Record<string, unknown>;

    // Mastodon outbox is an ActivityStreams OrderedCollection
    const items: unknown[] = (obj.orderedItems as unknown[]) ?? [];

    return items
      .map((item: unknown, idx: number): ImportedPost | null => {
        const activity = item as Record<string, unknown>;
        if (activity.type !== 'Create') return null;

        const obj2 = activity.object as Record<string, unknown>;
        if (!obj2) return null;
        if (obj2.type !== 'Note') return null;

        // Strip HTML tags from content
        const rawHtml = (obj2.content ?? '') as string;
        const content = stripHtml(rawHtml).trim();
        if (!content) return null;

        const published = (obj2.published ?? activity.published ?? '') as string;
        const createdAt = published
          ? Math.floor(new Date(published).getTime() / 1000)
          : Math.floor(Date.now() / 1000);

        const id = (obj2.id ?? String(idx)) as string;

        return {
          id: `mastodon-${encodeId(id)}`,
          content,
          createdAt,
          source: 'Mastodon',
          url: id.startsWith('http') ? id : undefined,
          selected: true,
        };
      })
      .filter((p): p is ImportedPost => p !== null);
  } catch {
    return [];
  }
}

// ── Facebook ───────────────────────────────────────────────────────────────────

/**
 * Parse the your_posts_1.json (or your_posts__check_ins__photos_and_videos.json)
 * file from a Facebook data archive (JSON format).
 *
 * Each item looks like:
 *   { "timestamp": 1596000000, "data": [{ "post": "Hello world" }], "title": "..." }
 */
export function parseFacebookArchive(raw: string): ImportedPost[] {
  try {
    const data: unknown = JSON.parse(raw);
    if (!Array.isArray(data)) return [];

    return data
      .map((item: unknown, idx: number): ImportedPost | null => {
        const entry = item as Record<string, unknown>;

        // Extract text from data[].post
        let content = '';
        const dataArr = entry.data as Array<Record<string, unknown>> | undefined;
        if (Array.isArray(dataArr)) {
          for (const d of dataArr) {
            if (typeof d.post === 'string' && d.post.trim()) {
              content = d.post.trim();
              break;
            }
          }
        }

        // Fallback: sometimes text is directly in entry.post
        if (!content && typeof entry.post === 'string') {
          content = (entry.post as string).trim();
        }

        if (!content) return null;

        // Facebook uses Unix seconds for timestamp
        const createdAt =
          typeof entry.timestamp === 'number'
            ? entry.timestamp
            : Math.floor(Date.now() / 1000) - idx;

        return {
          id: `facebook-${idx}`,
          content,
          createdAt,
          source: 'Facebook',
          selected: true,
        };
      })
      .filter((p): p is ImportedPost => p !== null);
  } catch {
    return [];
  }
}

// ── LinkedIn ───────────────────────────────────────────────────────────────────

/**
 * Parse the Shares.csv file from a LinkedIn data export.
 *
 * Expected columns (case-insensitive):
 *   Date, ShareCommentary, ShareLink, SharedUrl, Visibility
 *
 * LinkedIn also wraps the whole file in notes/headers sometimes — we handle that
 * by skipping rows until we find the actual header row.
 */
export function parseLinkedInShares(raw: string): ImportedPost[] {
  try {
    const lines = raw.split('\n').filter((l) => l.trim());
    if (lines.length === 0) return [];

    // Find the header row (LinkedIn sometimes prepends notes)
    let headerIdx = 0;
    for (let i = 0; i < Math.min(lines.length, 10); i++) {
      const lower = lines[i].toLowerCase();
      if (lower.includes('sharecommentary') || lower.includes('share commentary') || lower.includes('date')) {
        headerIdx = i;
        break;
      }
    }

    const headers = parseCsvLine(lines[headerIdx]).map((h) =>
      h.trim().replace(/^"|"$/g, '').toLowerCase()
    );

    // Find relevant column indices
    const commentaryIdx = headers.findIndex((h) => h.includes('sharecommentary') || h.includes('commentary') || h.includes('text') || h.includes('content'));
    const dateIdx = headers.findIndex((h) => h === 'date' || h.includes('date'));
    const linkIdx = headers.findIndex((h) => h.includes('sharelink') || h.includes('link') || h.includes('url'));

    if (commentaryIdx === -1) return [];

    return lines
      .slice(headerIdx + 1)
      .map((line, idx): ImportedPost | null => {
        const cols = parseCsvLine(line).map((c) => c.trim().replace(/^"+|"+$/g, ''));
        const content = cols[commentaryIdx]?.trim() ?? '';
        if (!content) return null;

        const rawDate = dateIdx >= 0 ? cols[dateIdx]?.trim() : '';
        const createdAt = rawDate
          ? Math.floor(new Date(rawDate).getTime() / 1000) || Math.floor(Date.now() / 1000)
          : Math.floor(Date.now() / 1000) - idx;

        const url = linkIdx >= 0 ? cols[linkIdx]?.trim() : undefined;

        return {
          id: `linkedin-${idx}`,
          content,
          createdAt,
          source: 'LinkedIn',
          url: url && url.startsWith('http') ? url : undefined,
          selected: true,
        };
      })
      .filter((p): p is ImportedPost => p !== null);
  } catch {
    return [];
  }
}

// ── Telegram ───────────────────────────────────────────────────────────────────

/**
 * Parse result.json from a Telegram Desktop data export.
 *
 * Full export structure:
 *   { "chats": { "list": [{ "name", "type", "messages": [...] }] } }
 *
 * Single-chat export structure:
 *   { "name", "type", "messages": [...] }
 *
 * Each message has:
 *   { "id", "type", "date_unixtime", "from", "text", "text_entities" }
 *
 * The `text` field can be a plain string or an array of strings/entities.
 */
export function parseTelegramExport(raw: string): ImportedPost[] {
  try {
    const data = JSON.parse(raw) as Record<string, unknown>;

    // Collect messages from all chats
    let allMessages: Record<string, unknown>[] = [];

    // Full export: { chats: { list: [...] } }
    const chats = data.chats as Record<string, unknown> | undefined;
    if (chats && Array.isArray(chats.list)) {
      for (const chat of chats.list as Record<string, unknown>[]) {
        const msgs = chat.messages as Record<string, unknown>[] | undefined;
        if (Array.isArray(msgs)) {
          // Only include the user's own messages — skip other people's messages
          // We use from_id to filter, but if not available include all
          allMessages = allMessages.concat(msgs);
        }
      }
    }

    // Single-chat export: { name, type, messages: [...] }
    if (allMessages.length === 0 && Array.isArray(data.messages)) {
      allMessages = data.messages as Record<string, unknown>[];
    }

    return allMessages
      .map((msg, idx): ImportedPost | null => {
        // Only regular messages, not service messages
        if (msg.type !== 'message') return null;

        const text = extractTelegramText(msg.text);
        if (!text.trim()) return null;

        const unixStr = (msg.date_unixtime ?? '') as string;
        const createdAt = unixStr
          ? Number(unixStr)
          : Math.floor(Date.now() / 1000) - idx;

        const from = (msg.from ?? '') as string;

        return {
          id: `telegram-${msg.id ?? idx}`,
          content: text.trim(),
          createdAt,
          source: from ? `Telegram (${from})` : 'Telegram',
          selected: true,
        };
      })
      .filter((p): p is ImportedPost => p !== null);
  } catch {
    return [];
  }
}

/** Extract plain text from Telegram's text field (string or entity array) */
function extractTelegramText(text: unknown): string {
  if (typeof text === 'string') return text;
  if (!Array.isArray(text)) return '';
  return text
    .map((part: unknown) => {
      if (typeof part === 'string') return part;
      if (part && typeof part === 'object') {
        return ((part as Record<string, unknown>).text ?? '') as string;
      }
      return '';
    })
    .join('');
}

// ── Bluesky ────────────────────────────────────────────────────────────────────

/**
 * Parse a Bluesky post export JSON file.
 *
 * The official Bluesky export is a binary .car file, not parseable in the
 * browser. However third-party tools (e.g. bskyarchive, bluesky-personal-data-
 * parser-json) produce an aggregated JSON array of post records like:
 *
 *   [{ "$type": "app.bsky.feed.post", "text": "Hello!", "createdAt": "2024-..." }]
 *
 * We also handle the firehose JSONL format where each line is a JSON object
 * with a nested record: { "record": { "text": "...", "createdAt": "..." } }
 */
export function parseBlueskyExport(raw: string): ImportedPost[] {
  try {
    // Try as a JSON array first
    const data: unknown = JSON.parse(raw);
    if (Array.isArray(data)) {
      return data
        .map((item: unknown, idx: number): ImportedPost | null => {
          const obj = item as Record<string, unknown>;

          // Handle nested record wrapper (firehose / archive format)
          const record = (obj.record ?? obj.value ?? obj) as Record<string, unknown>;

          // Must be a feed post
          const type = (record['$type'] ?? obj['$type'] ?? '') as string;
          if (type && !type.includes('feed.post')) return null;

          const text = (record.text ?? '') as string;
          if (!text.trim()) return null;

          // Skip replies
          if (record.reply) return null;

          const rawDate = (record.createdAt ?? obj.createdAt ?? '') as string;
          const createdAt = rawDate
            ? Math.floor(new Date(rawDate).getTime() / 1000)
            : Math.floor(Date.now() / 1000) - idx;

          const uri = (obj.uri ?? '') as string;

          return {
            id: `bluesky-${idx}`,
            content: text.trim(),
            createdAt,
            source: 'Bluesky',
            url: uri.startsWith('at://') ? `https://bsky.app/profile/${uri.split('/')[2]}/post/${uri.split('/').pop()}` : undefined,
            selected: true,
          };
        })
        .filter((p): p is ImportedPost => p !== null);
    }
  } catch {
    // Not valid JSON — try JSONL (one JSON object per line)
  }

  // Try JSONL format
  const lines = raw.split('\n').filter((l) => l.trim());
  const results: ImportedPost[] = [];
  for (let idx = 0; idx < lines.length; idx++) {
    try {
      const obj = JSON.parse(lines[idx]) as Record<string, unknown>;
      const record = (obj.record ?? obj.value ?? obj) as Record<string, unknown>;
      const type = (record['$type'] ?? obj['$type'] ?? '') as string;
      if (type && !type.includes('feed.post')) continue;
      const text = (record.text ?? '') as string;
      if (!text.trim()) continue;
      if (record.reply) continue;
      const rawDate = (record.createdAt ?? obj.createdAt ?? '') as string;
      const createdAt = rawDate
        ? Math.floor(new Date(rawDate).getTime() / 1000)
        : Math.floor(Date.now() / 1000) - idx;
      results.push({
        id: `bluesky-${idx}`,
        content: text.trim(),
        createdAt,
        source: 'Bluesky',
        selected: true,
      });
    } catch {
      continue;
    }
  }
  return results;
}

// ── Reddit ─────────────────────────────────────────────────────────────────────

/**
 * Parse posts.csv from a Reddit data export.
 *
 * Expected columns: id, permalink, url, title, selftext, score, created_utc, subreddit
 * We combine title + body into the post content.
 */
export function parseRedditPosts(raw: string): ImportedPost[] {
  try {
    const lines = raw.split('\n').filter((l) => l.trim());
    if (lines.length < 2) return [];

    const headers = parseCsvLine(lines[0]).map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const titleIdx = headers.findIndex((h) => h === 'title');
    const bodyIdx = headers.findIndex((h) => h === 'selftext' || h === 'body' || h === 'text');
    const dateIdx = headers.findIndex((h) => h === 'created_utc' || h === 'date' || h.includes('date'));
    const permalinkIdx = headers.findIndex((h) => h === 'permalink' || h === 'url');
    const subredditIdx = headers.findIndex((h) => h === 'subreddit');

    if (titleIdx === -1 && bodyIdx === -1) return [];

    return lines
      .slice(1)
      .map((line, idx): ImportedPost | null => {
        const cols = parseCsvLine(line).map((c) => c.trim().replace(/^"|"$/g, ''));

        const title = titleIdx >= 0 ? cols[titleIdx]?.trim() ?? '' : '';
        const body = bodyIdx >= 0 ? cols[bodyIdx]?.trim() ?? '' : '';
        const subreddit = subredditIdx >= 0 ? cols[subredditIdx]?.trim() ?? '' : '';

        // Skip deleted/removed posts
        if (body === '[deleted]' || body === '[removed]') return null;

        // Build content: "r/subreddit\n\nTitle\n\nBody" or just title if no body
        let content = '';
        if (title && body) {
          content = subreddit ? `r/${subreddit}\n\n${title}\n\n${body}` : `${title}\n\n${body}`;
        } else if (title) {
          content = subreddit ? `r/${subreddit}: ${title}` : title;
        } else {
          content = body;
        }
        if (!content.trim()) return null;

        const rawDate = dateIdx >= 0 ? cols[dateIdx]?.trim() : '';
        // Reddit uses unix seconds for created_utc
        const createdAt = rawDate && !isNaN(Number(rawDate))
          ? Number(rawDate)
          : rawDate ? Math.floor(new Date(rawDate).getTime() / 1000) || Math.floor(Date.now() / 1000)
          : Math.floor(Date.now() / 1000) - idx;

        const permalink = permalinkIdx >= 0 ? cols[permalinkIdx]?.trim() : '';
        const url = permalink
          ? (permalink.startsWith('http') ? permalink : `https://reddit.com${permalink}`)
          : undefined;

        return {
          id: `reddit-post-${idx}`,
          content,
          createdAt,
          source: 'Reddit',
          url,
          selected: true,
        };
      })
      .filter((p): p is ImportedPost => p !== null);
  } catch {
    return [];
  }
}

/**
 * Parse comments.csv from a Reddit data export.
 *
 * Expected columns: id, permalink, date, ip, subreddit, gildings, link, parent, body, score
 */
export function parseRedditComments(raw: string): ImportedPost[] {
  try {
    const lines = raw.split('\n').filter((l) => l.trim());
    if (lines.length < 2) return [];

    const headers = parseCsvLine(lines[0]).map((h) => h.trim().replace(/^"|"$/g, '').toLowerCase());
    const bodyIdx = headers.findIndex((h) => h === 'body');
    const dateIdx = headers.findIndex((h) => h === 'date' || h === 'created_utc' || h.includes('date'));
    const subredditIdx = headers.findIndex((h) => h === 'subreddit');
    const permalinkIdx = headers.findIndex((h) => h === 'permalink');

    if (bodyIdx === -1) return [];

    return lines
      .slice(1)
      .map((line, idx): ImportedPost | null => {
        const cols = parseCsvLine(line).map((c) => c.trim().replace(/^"|"$/g, ''));
        const body = cols[bodyIdx]?.trim() ?? '';

        if (!body || body === '[deleted]' || body === '[removed]') return null;

        const subreddit = subredditIdx >= 0 ? cols[subredditIdx]?.trim() ?? '' : '';
        const content = subreddit ? `r/${subreddit}: ${body}` : body;

        const rawDate = dateIdx >= 0 ? cols[dateIdx]?.trim() : '';
        const createdAt = rawDate && !isNaN(Number(rawDate))
          ? Number(rawDate)
          : rawDate ? Math.floor(new Date(rawDate).getTime() / 1000) || Math.floor(Date.now() / 1000)
          : Math.floor(Date.now() / 1000) - idx;

        const permalink = permalinkIdx >= 0 ? cols[permalinkIdx]?.trim() : '';
        const url = permalink
          ? (permalink.startsWith('http') ? permalink : `https://reddit.com${permalink}`)
          : undefined;

        return {
          id: `reddit-comment-${idx}`,
          content,
          createdAt,
          source: 'Reddit',
          url,
          selected: true,
        };
      })
      .filter((p): p is ImportedPost => p !== null);
  } catch {
    return [];
  }
}

// ── Plain-text / one-post-per-line ────────────────────────────────────────────

/**
 * Parse a plain text file where each non-empty line becomes one post.
 */
export function parsePlainText(raw: string): ImportedPost[] {
  return raw
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .map((line, idx): ImportedPost => ({
      id: `text-${idx}`,
      content: line,
      createdAt: Math.floor(Date.now() / 1000) - idx,
      source: 'Plain text',
      selected: true,
    }));
}

// ── CSV  ───────────────────────────────────────────────────────────────────────

/**
 * Parse a simple CSV where the first column is the post content.
 * Optionally a second column can be a date string.
 */
export function parseCsv(raw: string): ImportedPost[] {
  const lines = raw.split('\n').filter((l) => l.trim());
  // If there's a header row starting with "text" or "content", skip it
  const startIdx =
    lines.length > 0 &&
    /^(text|content|post|message|body)/i.test(lines[0])
      ? 1
      : 0;

  return lines.slice(startIdx).map((line, idx): ImportedPost => {
    const cols = parseCsvLine(line);
    const content = (cols[0] ?? '').trim().replace(/^"|"$/g, '');
    const rawDate = cols[1]?.trim().replace(/^"|"$/g, '');
    const createdAt = rawDate
      ? Math.floor(new Date(rawDate).getTime() / 1000) || Math.floor(Date.now() / 1000)
      : Math.floor(Date.now() / 1000) - idx;

    return {
      id: `csv-${idx}`,
      content,
      createdAt,
      source: 'CSV',
      selected: true,
    };
  });
}

// ── Auto-detect ────────────────────────────────────────────────────────────────

/** Detect format from filename and content, then parse accordingly. */
export function autoParseFile(filename: string, content: string): ImportedPost[] {
  const lower = filename.toLowerCase();

  if (lower.endsWith('.json') || lower.endsWith('.js') || lower.endsWith('.jsonl')) {
    // Try Twitter archive format
    if (content.includes('window.YTD') || content.includes('full_text')) {
      const result = parseTwitterArchive(content);
      if (result.length > 0) return result;
    }
    // Try Mastodon outbox
    if (content.includes('orderedItems') || content.includes('OrderedCollection')) {
      const result = parseMastodonExport(content);
      if (result.length > 0) return result;
    }
    // Try Facebook archive (your_posts_1.json)
    if (
      lower.includes('your_posts') ||
      lower.includes('facebook') ||
      (content.includes('"timestamp"') && content.includes('"data"'))
    ) {
      const result = parseFacebookArchive(content);
      if (result.length > 0) return result;
    }
    // Try Telegram export (result.json)
    if (
      lower.includes('result') ||
      lower.includes('telegram') ||
      content.includes('"chats"') && content.includes('"messages"') ||
      content.includes('"date_unixtime"')
    ) {
      const result = parseTelegramExport(content);
      if (result.length > 0) return result;
    }
    // Try Bluesky export (app_bsky_feed_post.json or JSONL firehose)
    if (
      lower.includes('bsky') ||
      lower.includes('bluesky') ||
      lower.includes('app_bsky') ||
      content.includes('app.bsky.feed.post') ||
      lower.endsWith('.jsonl')
    ) {
      const result = parseBlueskyExport(content);
      if (result.length > 0) return result;
    }
    // Generic JSON array of strings or objects with "content"/"text"
    try {
      const data = JSON.parse(content);
      if (Array.isArray(data)) {
        // If items have $type it might be Bluesky — try that parser
        if ((data[0] as Record<string, unknown>)?.['$type']) {
          const result = parseBlueskyExport(content);
          if (result.length > 0) return result;
        }
        return data
          .map((item: unknown, idx: number): ImportedPost | null => {
            if (typeof item === 'string' && item.trim()) {
              return { id: `json-${idx}`, content: item.trim(), createdAt: Math.floor(Date.now() / 1000), source: 'JSON', selected: true };
            }
            const obj = item as Record<string, unknown>;
            const text = (obj.content ?? obj.text ?? obj.message ?? obj.body ?? '') as string;
            if (text.trim()) {
              return { id: `json-${idx}`, content: text.trim(), createdAt: Math.floor(Date.now() / 1000), source: 'JSON', selected: true };
            }
            return null;
          })
          .filter((p): p is ImportedPost => p !== null);
      }
    } catch {
      /* fall through */
    }
  }

  if (lower.endsWith('.csv')) {
    // Try LinkedIn Shares.csv
    if (
      lower.includes('shares') ||
      lower.includes('linkedin') ||
      content.toLowerCase().includes('sharecommentary')
    ) {
      const result = parseLinkedInShares(content);
      if (result.length > 0) return result;
    }
    // Try Reddit posts.csv
    if (
      lower.includes('posts') ||
      lower.includes('reddit') ||
      content.toLowerCase().includes('selftext') ||
      content.toLowerCase().includes('subreddit')
    ) {
      // Try posts first, then comments
      const postResult = parseRedditPosts(content);
      if (postResult.length > 0) return postResult;
      const commentResult = parseRedditComments(content);
      if (commentResult.length > 0) return commentResult;
    }
    // Try Reddit comments.csv
    if (lower.includes('comment')) {
      const result = parseRedditComments(content);
      if (result.length > 0) return result;
    }
    return parseCsv(content);
  }

  // Default: plain text
  return parsePlainText(content);
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function stripHtml(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ');
}

function encodeId(id: string): string {
  return id.replace(/[^a-zA-Z0-9]/g, '_').slice(-32);
}

function parseCsvLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}
